<template>
  <div class="question-left-wrap medium-12 content-box bg-light">
    <div class="row question-left-header">
      <div class="column">
        <h2 class="page-sub-title">
          {{ $t('SOURCES.SIDEBAR_ITEMS.QUESTION') }}
        </h2>
        <span class="message row">{{
          $t('QUESTION_SOURCES.DESCRIPTION')
        }}</span>
      </div>
      <div class="column btn-upload-document">
        <woot-button
          color-scheme="success"
          icon="add-circle"
          @click="openCreateQuestionPopup"
        >
          {{ $t('QUESTION_SOURCES.ACTIONS.ADD.TITLE') }}
        </woot-button>
      </div>
    </div>
    <header>
      <div class="header-left--wrap">
        <label class="sub-block-title header-text">{{
          $t('QUESTION_SOURCES.SORT_BY.TITLE')
        }}</label>
        <woot-button
          size="small"
          variant="smooth"
          color-scheme="primary"
          class-names="sub-block-title header-text"
          :class="{
            'is-active':
              sortBy === QUESTION_SOURCES_SORTBY.createdAt &&
              sortOrder === SOURCES_SORT_ORDER.desc,
          }"
          @click="
            () =>
              this.$emit(
                'on-update-sortby',
                QUESTION_SOURCES_SORTBY.createdAt,
                SOURCES_SORT_ORDER.desc
              )
          "
        >
          {{ $t('QUESTION_SOURCES.SORT_BY.NEWEST') }}
        </woot-button>
        <woot-button
          size="small"
          variant="smooth"
          color-scheme="primary"
          class-names="sub-block-title header-text"
          :class="{
            'is-active':
              sortBy === QUESTION_SOURCES_SORTBY.createdAt &&
              sortOrder === SOURCES_SORT_ORDER.asc,
          }"
          @click="
            () =>
              this.$emit(
                'on-update-sortby',
                QUESTION_SOURCES_SORTBY.createdAt,
                SOURCES_SORT_ORDER.asc
              )
          "
        >
          {{ $t('QUESTION_SOURCES.SORT_BY.OLDEST') }}
        </woot-button>
        <woot-button
          size="small"
          variant="smooth"
          color-scheme="primary"
          class-names="sub-block-title header-text"
          :class="{
            'is-active': sortBy === QUESTION_SOURCES_SORTBY.unresolved,
          }"
          @click="
            () =>
              this.$emit(
                'on-update-sortby',
                QUESTION_SOURCES_SORTBY.unresolved,
                SOURCES_SORT_ORDER.desc
              )
          "
        >
          {{ $t('QUESTION_SOURCES.SORT_BY.UNRESOLVED') }} ({{
            countUnresolved
          }})
        </woot-button>
      </div>
      <div class="header-right--wrap">
        <woot-button
          size="small"
          variant="smooth"
          color-scheme="primary"
          class-names="sub-block-title header-text"
          @click="
            () => (this.showDropdownCategories = !this.showDropdownCategories)
          "
        >
          <b>{{ $t('QUESTION_SOURCES.FILTER') }}</b> {{ selectedCategory.name }}
        </woot-button>
        <woot-dropdown-menu
          v-if="showDropdownCategories"
          class="filter-dropdown"
        >
          <woot-dropdown-sub-menu
            :title="this.$t('QUESTION_SOURCES.CATEGORY')"
            class-name="primary filter-dropdown"
          >
            <woot-dropdown-item v-for="option in categories" :key="option.id">
              <woot-button
                size="small"
                variant="smooth"
                color-scheme="primary"
                class-names="sub-block-title header-text button-question-dropdown"
                @click="() => onSelectFilterCategory(option)"
              >
                <div class="categories">
                  <input
                    id="checkbox"
                    type="checkbox"
                    :checked="selectedCategory.name === option.name"
                  />
                  <div class="category-detail">
                    <span>
                      {{ option.name }}
                    </span>
                    <span>
                      {{ option.question_count }}
                    </span>
                  </div>
                </div>
              </woot-button>
            </woot-dropdown-item>
          </woot-dropdown-sub-menu>
        </woot-dropdown-menu>
      </div>
    </header>
    <section class="questions-table-wrap">
      <table class="woot-table">
        <thead>
          <th width="30%">{{ $t('QUESTION_SOURCES.TABLE.HEADER.QUESTION') }}</th>
          <th width="35%">{{ $t('QUESTION_SOURCES.TABLE.HEADER.RESPONSE') }}</th>
          <th>{{ $t('QUESTION_SOURCES.TABLE.HEADER.CATEGORY') }}</th>
          <th>{{ $t('QUESTION_SOURCES.TABLE.HEADER.UPDATED') }}</th>
          <th>{{ $t('QUESTION_SOURCES.TABLE.HEADER.ACTIONS') }}</th>
        </thead>
        <tbody>
          <tr
            v-for="row in tableData"
            :key="row.id"
            :class="{ bubble: row.status === QUESTION_SOURCES_STATES.pending }"
          >
            <td>
              {{ row.node.question }}
            </td>
            <td>
              {{ row.node.answer }}
            </td>
            <td>
              {{ renderCategory(row.category) }}
            </td>
            <td>
              {{ formatDate(row.updated_at) }} <br>
              by {{ row.user.name }} <br>
              {{
                row.status === QUESTION_SOURCES_STATES.pending
                  ? '(Unresolved)'
                  : ''
              }}
            </td>
            <td>
              <woot-button
                v-tooltip="$t('QUESTION_SOURCES.ACTIONS.EDIT.BUTTON_TEXT')"
                variant="smooth"
                size="tiny"
                color-scheme="secondary"
                class-names="grey-btn"
                icon="edit"
                @click="
                  openEditQuestionPopup(
                    row.id,
                    row.node.question,
                    row.node.answer,
                    renderCategory(row.category)
                  )
                "
              />
              <woot-button
                v-if="row.status !== QUESTION_SOURCES_STATES.pending"
                v-tooltip="
                  $t(
                    'QUESTION_SOURCES.ACTIONS.TOGGLE_ENABLED.BUTTON_DISABLE_TEXT'
                  )
                "
                size="tiny"
                color-scheme="secondary"
                class-names="grey-btn"
                variant="smooth"
                icon="warning"
                @click="() => $emit('on-toggle-enabled-click', row)"
              />
              <woot-button
                v-else
                v-tooltip="
                  $t(
                    'QUESTION_SOURCES.ACTIONS.TOGGLE_ENABLED.BUTTON_ENABLE_TEXT'
                  )
                "
                size="tiny"
                color-scheme="success"
                class-names="grey-btn"
                variant="smooth"
                icon="checkmark"
                @click="() => $emit('on-toggle-enabled-click', row)"
              />
              <woot-button
                v-tooltip="$t('QUESTION_SOURCES.ACTIONS.DELETE.BUTTON_TEXT')"
                variant="smooth"
                color-scheme="alert"
                size="tiny"
                icon="dismiss-circle"
                class-names="grey-btn"
                @click="() => $emit('on-delete-click', row)"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <woot-loading-state
        v-if="isLoading"
        :message="$t('DOCUMENT_SOURCES.TABLE.LOADING')"
      />
      <div
        v-else-if="!isLoading && !tableData.length"
        class="question--empty-records"
      >
        <spinner />
        <span>{{ $t('QUESTION_SOURCES.TABLE.EMPTY_STATE') }}</span>
      </div>
    </section>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import Spinner from 'shared/components/Spinner.vue';
import timeMixin from 'dashboard/mixins/time';
import rtlMixin from 'shared/mixins/rtlMixin';
import { formatDateString } from 'shared/helpers/DateHelper';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import WootDropdownSubMenu from 'shared/components/ui/dropdown/DropdownSubMenu.vue';
import {
  QUESTION_SOURCES_SORTBY,
  SOURCES_SORT_ORDER,
  QUESTION_SOURCES_STATES,
} from '../constants';

export default {
  components: {
    Spinner,
    WootDropdownMenu,
    WootDropdownItem,
    WootDropdownSubMenu,
  },
  mixins: [clickaway, timeMixin, rtlMixin],
  props: {
    questions: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: String,
      default: '',
    },
    sortOrder: {
      type: String,
      default: '',
    },
    countUnresolved: {
      type: Number,
      default: 0,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    selectedCategory: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showAddPopup: false,
      showDropdownCategories: false,
      QUESTION_SOURCES_SORTBY: QUESTION_SOURCES_SORTBY,
      QUESTION_SOURCES_STATES: QUESTION_SOURCES_STATES,
      SOURCES_SORT_ORDER: SOURCES_SORT_ORDER,
    };
  },
  computed: {
    tableData() {
      if (this.isLoading) {
        return [];
      }
      return this.questions;
    },
  },
  methods: {
    onUpdateSortby(sortBy, sortOrder) {
      let page = this.meta.currentPage;
      if (sortBy !== this.meta.sortBy || sortOrder !== this.meta.sortOrder) {
        page = DEFAULT_PAGE;
      }
      this.fetchQuestions(page, sortBy, sortOrder);
    },
    openCreateQuestionPopup() {
      this.$emit('show-create-question');
    },
    openEditQuestionPopup(id, question, response, category) {
      this.$emit('show-edit-question', {
        id,
        question,
        response,
        category,
      });
    },
    formatDate(date) {
      return formatDateString(date);
    },
    onSelectFilterCategory(row) {
      this.$emit('on-update-filter-category', row);
      this.showDropdownCategories = false;
    },
    renderCategory(category) {
      return category?.name || 'Uncategorized';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/mixins';

.questions-table-wrap {
  flex: 1 1;
  height: 100%;
  tr {
    vertical-align: top;
  }
  tbody td {
    padding-top: var(--space-medium);
    padding-bottom: var(--space-medium);
  }
}

.question--empty-records {
  align-items: center;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  font-size: var(--font-size-mini);
  height: 7rem;
  justify-content: center;
  width: 100%;
}

.btn-upload-document {
  flex: initial;
}
.question-left-wrap {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  margin-left: var(--space-large);
  margin-right: var(--space-large);
  min-height: 80vh;
}

.woot-table {
  width: 100%;
  margin-top: var(--space-small);
  table-layout: fixed;
}
.bubble {
  color: var(--v-400);
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--space-normal);
  margin-bottom: var(--space-normal);

  .header-left--wrap {
    display: flex;
    align-items: center;
    width: 100%;

    .header-text {
      margin-right: var(--space-slab);
      margin-bottom: 0;
      color: var(--w-500);
    }
    .is-active {
      background-color: var(--w-500);
      color: var(--white);
    }
  }

  .header-right--wrap {
    flex: none;
    align-items: center;
    position: relative;
    ul {
      position: absolute;
      width: 100%;
      background-color: var(--w-50);
      margin-top: var(--space-small);
    }
    .header-text {
      color: var(--w-500);
      width: 20rem;

      &.button-question-dropdown {
        width: 100%;
        min-width: 20em;
      }
    }
    .categories {
      display: inline-flex;
      align-items: center;
      width: 100%;
    }
    .category-detail {
      display: flex;
      justify-content: space-between;
      width: 100%;
      padding-right: var(--space-one);

      span {
        white-space: initial;
      }
    }
  }
  .select-locale {
    height: var(--space-large);
    margin-bottom: 0;
    padding-top: var(--space-micro);
    padding-bottom: var(--space-micro);
  }
}

[type='checkbox'] {
  width: var(--space-normal);
  height: var(--space-normal);
  color: var(--w-500);
  vertical-align: middle;
  -webkit-appearance: none;
  border-radius: 50%;
  background-color: var(--w-50);
  transition: background 300ms;
  cursor: pointer;
  margin-right: var(--space-small);
}

[type='checkbox']::before {
  content: '';
  color: transparent;
  display: block;
  width: inherit;
  height: inherit;
  border-radius: inherit;
  border: 0;
  background-color: transparent;
  background-size: contain;
  box-shadow: inset 0 0 0 1px var(--w-500);
}

[type='checkbox']:checked {
  background-color: currentcolor;
}

[type='checkbox']:checked::before {
  box-shadow: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E %3Cpath d='M15.88 8.29L10 14.17l-1.88-1.88a.996.996 0 1 0-1.41 1.41l2.59 2.59c.39.39 1.02.39 1.41 0L17.3 9.7a.996.996 0 0 0 0-1.41c-.39-.39-1.03-.39-1.42 0z' fill='%23fff'/%3E %3C/svg%3E");
}
</style>
